import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { titleCase, fmtDateTime } from "@/lib/format";

function isIsoDate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v);
}

// Technical / noisy keys that should never surface in a human-facing drawer.
const HIDDEN_KEYS = new Set(["id", "_id", "__v", "nameKey", "searchKey"]);
const HIDDEN_PATTERN = /(^_)|dataUri$|Path$|Base64$/i;

const isEmpty = (v) => v === null || v === undefined || v === "";

// Compact, readable rendering of nested objects/arrays (no raw JSON dumps).
function compact(v, depth = 0) {
  if (isEmpty(v)) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isIsoDate(v)) return fmtDateTime(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return "Empty";
    if (v.every((x) => typeof x !== "object" || x === null)) return v.join(", ");
    return `${v.length} item${v.length > 1 ? "s" : ""}`;
  }
  if (typeof v === "object") {
    const entries = Object.entries(v).filter(([, val]) => !isEmpty(val));
    if (entries.length === 0) return "Not set";
    if (depth >= 1) return `${entries.length} field${entries.length > 1 ? "s" : ""}`;
    return null; // rendered as nested rows by NestedObject
  }
  return String(v);
}

function NestedObject({ obj }) {
  const entries = Object.entries(obj).filter(([k]) => !HIDDEN_KEYS.has(k) && !HIDDEN_PATTERN.test(k));
  return (
    <div className="mt-1 space-y-1 rounded-md border border-stone-200 bg-stone-50/60 p-2">
      {entries.map(([k, val]) => (
        <div key={k} className="flex items-start justify-between gap-3 text-xs">
          <span className="text-stone-500">{titleCase(k)}</span>
          <span className="text-right text-stone-800">{compact(val, 1)}</span>
        </div>
      ))}
    </div>
  );
}

function Value({ v }) {
  if (isEmpty(v)) return <span className="text-muted-foreground">—</span>;
  if (typeof v === "boolean") return <span>{v ? "Yes" : "No"}</span>;
  if (isIsoDate(v)) return <span>{fmtDateTime(v)}</span>;
  if (Array.isArray(v)) return <span className="text-stone-800">{compact(v)}</span>;
  if (typeof v === "object") return <NestedObject obj={v} />;
  return <span className="text-stone-800 break-words">{String(v)}</span>;
}

export function DetailDrawer({ open, onOpenChange, title, row, onEdit, actions, children }) {
  const entries = row
    ? Object.entries(row).filter(([k]) => !HIDDEN_KEYS.has(k) && !HIDDEN_PATTERN.test(k))
    : [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="detail-drawer" className="w-full sm:max-w-xl bg-white p-0">
        <SheetHeader className="flex flex-row items-center justify-between border-b border-stone-200 px-6 py-4">
          <SheetTitle className="font-display text-lg text-stone-900">{title || "Details"}</SheetTitle>
          <SheetDescription className="sr-only">Record details</SheetDescription>
          {onEdit && (
            <Button data-testid="drawer-edit" size="sm" variant="outline" className="mr-8 gap-1.5" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
        </SheetHeader>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 bg-stone-50/70 px-6 py-3">
            {actions}
          </div>
        )}
        <ScrollArea className="h-[calc(100vh-72px)]">
          <div className="divide-y divide-stone-100">
            {entries.map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-3 px-6 py-2.5">
                <div className="col-span-1 text-xs font-medium uppercase tracking-wide text-stone-500">
                  {titleCase(k)}
                </div>
                <div className="col-span-2 text-sm">
                  {/status|qastatus|priceType|priority/i.test(k) && typeof v !== "object" ? (
                    <StatusBadge value={v} />
                  ) : (
                    <Value v={v} />
                  )}
                </div>
              </div>
            ))}
          </div>
          {children && <div className="border-t border-stone-200 px-6 py-4">{children}</div>}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
